import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/home.css';

const Home = () => {
  // Featured datasets for the carousel
  const featuredDatasets = [
    {
      id: 1,
      name: 'MNIST Dataset',
      description: 'Handwritten digits dataset for image classification',
      category: 'Computer Vision',
      downloads: 1250,
      rating: 4.5
    },
    {
      id: 2,
      name: 'IMDb Reviews Dataset',
      description: 'Movie reviews for sentiment analysis',
      category: 'NLP',
      downloads: 875,
      rating: 4.2
    },
    {
      id: 3,
      name: 'COVID-19 Global Dataset',
      description: 'Comprehensive COVID-19 data including cases, deaths, and testing',
      category: 'Tabular',
      downloads: 3200,
      rating: 4.8
    }
  ];

  // Stats for the counter section
  const stats = [
    { value: '500+', label: 'Datasets' },
    { value: '10K+', label: 'Downloads' },
    { value: '2,500+', label: 'Users' },
    { value: '150+', label: 'Contributors' }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1>Decentralized Marketplace for AI Datasets</h1>
            <p className="hero-subtitle">
              Discover, share, and monetize high-quality datasets for machine learning and AI research with transparent tracking and fair revenue sharing.
            </p>
            <div className="hero-buttons">
              <Link to="/marketplace" className="btn btn-primary btn-lg">
                Explore Datasets
              </Link>
              <Link to="/upload" className="btn btn-secondary btn-lg">
                Share Your Data
              </Link>
              <Link to="/signup" className="btn btn-outline btn-lg">
                Sign Up Free
              </Link>
            </div>
          </div>
          <div className="hero-image">
            <img src="/images/hero-illustration.svg" alt="Data visualization" />
          </div>
        </div>
      </section>

      {/* Stats Counter Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Datasets Section */}
      <section className="featured-section">
        <div className="container">
          <div className="section-header">
            <h2>Featured Datasets</h2>
            <Link to="/marketplace" className="view-all-link">View All <i className="fas fa-arrow-right"></i></Link>
          </div>
          <div className="featured-grid">
            {featuredDatasets.map(dataset => (
              <div key={dataset.id} className="featured-card">
                <div className="featured-card-content">
                  <span className="category-badge">{dataset.category}</span>
                  <h3 className="featured-title">
                    <Link to={`/dataset/${dataset.id}`}>{dataset.name}</Link>
                  </h3>
                  <p className="featured-description">{dataset.description}</p>
                  <div className="featured-meta">
                    <span className="featured-downloads">
                      <i className="fas fa-download"></i> {dataset.downloads}
                    </span>
                    <span className="featured-rating">
                      <i className="fas fa-star"></i> {dataset.rating}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Why Choose DataHarbor?</h2>
            <p className="section-subtitle">Our platform offers unique benefits for AI researchers and data scientists</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-database"></i>
              </div>
              <h3>Decentralized Storage</h3>
              <p>Store your datasets and models securely on decentralized networks with immutable records</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-link"></i>
              </div>
              <h3>On-chain Tracking</h3>
              <p>Transparent ownership and usage tracking via blockchain technology</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-hand-holding-usd"></i>
              </div>
              <h3>Fair Revenue Sharing</h3>
              <p>Automatic royalty distribution to creators and contributors</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fas fa-star"></i>
              </div>
              <h3>Reputation System</h3>
              <p>Build your reputation as a quality dataset or model provider</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>How It Works</h2>
            <p className="section-subtitle">Get started with DataHarbor in three simple steps</p>
          </div>
          <div className="steps-container">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-icon">
                <i className="fas fa-wallet"></i>
              </div>
              <h3>Connect Wallet</h3>
              <p>Connect your Web3 wallet to get started with our platform</p>
            </div>
            <div className="step-connector"></div>
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-icon">
                <i className="fas fa-cloud-upload-alt"></i>
              </div>
              <h3>Upload or Browse</h3>
              <p>Share your datasets/models or explore what others have shared</p>
            </div>
            <div className="step-connector"></div>
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-icon">
                <i className="fas fa-exchange-alt"></i>
              </div>
              <h3>Purchase or Sell</h3>
              <p>Buy access to datasets/models or earn from your contributions</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Get Started?</h2>
            <p>Join our community of data scientists and AI researchers today</p>
            <div className="cta-buttons">
              <Link to="/marketplace" className="btn btn-primary btn-lg">
                Explore Datasets
              </Link>
              <Link to="/login" className="btn btn-outline btn-lg">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
